'use client'

import { Notification } from '@/features/notification/model/notification'
import NotificationService from '@/features/notification/notification-service'
import { useEnvironmentState } from '@/utils/environment/store/useEnvironmentState'
import { useGoogleAnalytics } from '@/utils/hooks/useGoogleAnalitics'
import { useModalState } from '@/utils/hooks/useModalState'
import clsx from 'clsx'
import { FaRegClock } from 'react-icons/fa6'
import { OpenIcon } from '../foundations/OpenIcon'
import { TrashIcon } from '../foundations/TrashIcon'

export interface NotificationItemProps {
	tabLabel: string
	notification: Notification
	read: (notificationId: number) => void
	cancel: (notificationId: number) => void
}

export const NotificationItem = (props: NotificationItemProps) => {
	const modalState = useModalState()
	const environment = useEnvironmentState()
	const { pushLabelClickedEvent } = useGoogleAnalytics()

	const handleOpenAttachmentClick = async () => {
		if (props.notification.documentFilepath) {
			const imagesEndpoint = environment.getStringOrThrow('WEBSPHERE_IMAGES_ENDPOINT')
			await openAttachmentAndRead(`${imagesEndpoint}/${props.notification.documentFilepath}`)
			return
		}

		const pdf = await download()
		if (!pdf) return
		await openAttachmentAndRead(`data:application/pdf;base64,${pdf}`)
	}

	const download = async (): Promise<string | undefined> => {
		try {
			return await NotificationService.download({ notificationId: props.notification.id })
		} catch (error: any) {
			console.error('Notification downlaod', error)
			return undefined
		}
	}

	const openAttachmentAndRead = async (attachmentUrl: string) => {
		modalState.openNotificationAttachmentModal({ attachmentUrl })
		props.read(props.notification.id)
		pushLabelClickedEvent({ event: 'privatenotification', eventName: 'private_notification', label_clicked: props.notification.title, cta_clicked: 'Go to', section: props.tabLabel })
	}

	const handleCancelClick = async () => {
		props.cancel(props.notification.id)
		pushLabelClickedEvent({ event: 'privatenotification', eventName: 'private_notification', label_clicked: props.notification.title, cta_clicked: 'Erase', section: props.tabLabel })
	}

	return (
		<li className="w-full first:rounded-t last:rounded-b border border-[#ddd] border-t-transparent first:border-t-[#ddd]">
			<div
				className={clsx('flex flex-row items-center gap-2 py-[11.25px] px-[18.75px]', {
					'border-l-4 border-msc-blue': !props.notification.isRead,
				})}
			>
				<div>
					<p className="text-msc-blue text-sm font-bold">{props.notification.title}</p>
					<p className="text-msc-blue text-sm">{props.notification.description}</p>
					<p className="text-msc-blue text-[10.5px] flex flex-row items-center gap-2">
						<FaRegClock color="#000033" />
						<span>{props.notification.timestamp}</span>
					</p>
				</div>
				<div className="flex flex-col ml-auto gap-4 justify-content-around">
					<div className="cursor-pointer" onClick={handleOpenAttachmentClick}>
						<OpenIcon color="#000033" />
					</div>
					<div className="cursor-pointer" onClick={handleCancelClick}>
						<TrashIcon color="#000033" />
					</div>
				</div>
			</div>
		</li>
	)
}
